import React, { useEffect, useState, useCallback } from "react";
import LoadingInComponent from "../../Components/LoadingInComponent";
import Button from "../../Components/Button";
import WidgetCard from "../../Components/WidgetCard";
import ApexCharts from "react-apexcharts";
import axios from "axios";
import {
  CREATE_DIETARY_API,
  EXPORT_DIETARY_REPORT_API,
  GET_DIETARY_LIST_API,
  GET_DIETARY_WIDGET_API,
} from "../../Constant/api";
import { useToast } from "../../Components/Toast";
import Modal from "../../Components/Modal";
import TextboxSet from "../../Components/TextboxSet";
import DietaryTable from "../../Components/Tables/DietaryTable";
import fileDownload from "js-file-download";
import Breadcrumb from "../../Components/Breadcrumb";
import { motion as m } from "framer-motion";
import Can from "../../Components/Can";

const DietaryList = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchCleared, setIsSearchCleared] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [widgetData, setWidgetData] = useState({});
  const [tableData, setTableData] = useState([]);
  const [meta, setMeta] = useState({});
  const [dietaryName, setDietaryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartHeight, setChartHeight] = useState(0);
  const [chartOptions, setChartOptions] = useState({
    legend: {
      onItemClick: {
        toggleDataSeries: false,
      },
      onItemHover: {
        highlightDataSeries: false,
      },
    },
    xaxis: {
      categories: [],
    },
  });
  const [perPage, setPerPage] = useState(10);

  const { toast } = useToast();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const clearFilter = () => {
    setSearchTerm("");
    setIsSearchCleared(true);
  };

  const handleOnChange = (e) => {
    setDietaryName(e.target.value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const fetchDietaryList = useCallback(
    async (page = 1, perPageValue = perPage) => {
      setLoading(true);
      try {
        axios
          .get(GET_DIETARY_LIST_API, {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
            },
            params: {
              eventId: sessionStorage.getItem("event_id"),
              page: page,
              search: searchTerm,
              perPage: perPageValue,
            },
          })
          .then((response) => {
            setLoading(false);
            setTableData(response.data.data);
            setMeta(response.data.meta.pagination);
          })
          .catch((error) => {
            setLoading(false);
            toast("Failed to fetch dietary list", "error");
            console.error(error);
          });
      } catch (error) {
        setLoading(false);
        toast("Failed to fetch dietary list", "error");
        console.error(error);
      }
    },
    [perPage, searchTerm, toast]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchDietaryList();
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(e.target.value);
    fetchDietaryList(1, e.target.value);
  };

  const handlePageClick = (e) => {
    fetchDietaryList(e.selected + 1);
  };

  const fetchWidgetData = useCallback(async () => {
    setLoading(true);
    axios
      .get(GET_DIETARY_WIDGET_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: {
          eventId: sessionStorage.getItem("event_id"),
        },
      })
      .then((response) => {
        setLoading(false);
        setWidgetData(response.data.data);
        setChartSeries([
          {
            name: "Dietary Breakdown", // you can change the label here
            data: response.data.data.dietary_breakdown.data,
          },
        ]);

        setChartOptions({
          chart: {
            type: "bar",
          },
          xaxis: {
            categories: response.data.data.dietary_breakdown.labels,
          },
          plotOptions: {
            bar: {
              horizontal: true,
              borderRadius: 4,
            },
          },
          colors: "#4F46E5",
        });

        const barCount = response.data.data.dietary_breakdown.labels.length;
        const calculatedHeight = barCount * 50;

        setChartHeight(calculatedHeight);
      })
      .catch((error) => {
        setLoading(false);
        toast("Failed to fetch widget data", "error");
        console.error(error);
      });
  }, [toast]);

  const handleAddDietary = () => {
    if (!dietaryName.trim()) {
      toast("Dietary name cannot be empty", "error");
      return;
    }
    axios
      .post(
        CREATE_DIETARY_API,
        {
          name: dietaryName,
          event_id: sessionStorage.getItem("event_id"),
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
        }
      )
      .then((response) => {
        toast("Dietary added successfully", "success");
        toggleModal();
        setDietaryName("");
        fetchDietaryList();
        fetchWidgetData();
      })
      .catch((error) => {
        toast(error.response.data.data[0], "error");
        console.error(error);
      });
  };

  const exportDietaryReport = async () => {
    try {
      axios
        .post(
          EXPORT_DIETARY_REPORT_API,
          { eventId: sessionStorage.getItem("event_id") },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
              TimezoneOffset: new Date().getTimezoneOffset(),
            },
            responseType: "blob",
          }
        )
        .then((res) => {
          fileDownload(res.data, "dietary_report.xlsx");
        })
        .catch((err) => {
          toast("Failed to export.", "error");
          console.log(err);
        });
    } catch (error) {
      toast("Failed to export.", "error");
      console.log(error);
    }
  };

  useEffect(() => {
    fetchWidgetData();
    fetchDietaryList();
  }, [fetchWidgetData, fetchDietaryList]);

  useEffect(() => {
    if (isSearchCleared && searchTerm === "") {
      fetchDietaryList();
      setIsSearchCleared(false);
    }
  }, [isSearchCleared, searchTerm, fetchDietaryList]);

  const breadcrumbItems = [
    { label: "Special Dietary Management" },
    { label: "List" },
  ];

  return (
    <div className="flex flex-col w-full h-full">
      {showModal && (
        <Modal
          title="Add Dietary"
          handleSubmit={handleAddDietary}
          submitLabel="Add Dietary"
          toggleModal={toggleModal}
        >
          <div className="w-full">
            <TextboxSet
              label="Dietary Name"
              name="dietary_name"
              value={dietaryName}
              onChange={(e) => handleOnChange(e)}
            />
          </div>
        </Modal>
      )}
      <LoadingInComponent isLoading={loading}>
        {!loading && (
          <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[12px] md:gap-[24px]">
            {/* Breadcrumb */}
            <Breadcrumb items={breadcrumbItems} />

            {/* Title */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-[12px] md:gap-[24px]">
              <h1 className="text-[29px]">Special Dietary Management</h1>
              <div className="flex gap-[8px]">
                <Can permission="export_special_dietary">
                  <Button variant="secondary" onClick={exportDietaryReport}>
                    Export Report
                  </Button>
                </Can>
                <Can permission="create_special_dietary">
                  <Button onClick={toggleModal}>Add Dietary</Button>
                </Can>
              </div>
            </div>

            {/* Widgets */}
            <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-[12px] md:gap-[24px]">
              <WidgetCard
                name="Total Dietary"
                data={widgetData.total_dietary}
              />
              <WidgetCard
                name="Total Requests"
                data={widgetData.total_requests}
              />
            </div>

            <m.div
              className="flex flex-col border border-[#EFF0F2] w-full h-fit p-[12px] md:p-[24px] gap-[12px] bg-white rounded-xl"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-[16px] px-[12px] pt-[12px] md:p-0">
                Dietary Requests Breakdown
              </h2>
              {chartSeries && chartSeries[0]?.data.length > 0 ? (
                <div>
                  <ApexCharts
                    options={chartOptions}
                    series={chartSeries}
                    type="bar"
                    width={"100%"}
                    height={chartHeight}
                  />
                </div>
              ) : (
                <div className="flex w-full h-[20vh] items-center justify-center">
                  <p className="text-[25px] text-gray-600">No data available</p>
                </div>
              )}
            </m.div>

            {/* Data */}
            <DietaryTable
              data={tableData}
              meta={meta}
              handlePageClick={handlePageClick}
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              handleKeyDown={handleKeyDown}
              perPage={perPage}
              handlePerPageChange={handlePerPageChange}
              clearFilter={clearFilter}
            />
          </div>
        )}
      </LoadingInComponent>
    </div>
  );
};

export default DietaryList;
